import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button,
  Autocomplete,
  CircularProgress,
  Alert,
  Stack,
  Chip
} from "@mui/material";
import Tesseract from "tesseract.js";
import { validate as validateContainerNo, suggest as suggestContainerNo, pretty as prettyContainerNo }
  from "../utils/containerNumber";
import api from "../services/api";

// ============================================================
// OCR HELPERS (kept outside component so they don't recreate on every render)
// ============================================================

// Resize + grayscale + contrast boost. Tesseract reads dramatically better
// on a clean high-contrast image than on a raw phone snapshot.
async function preprocessImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();

    const maxWidth = 1600;
    const scale = Math.min(1, maxWidth / img.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const adjusted = Math.min(255, Math.max(0, (gray - 128) * 1.5 + 128));
      data[i] = data[i + 1] = data[i + 2] = adjusted;
    }
    ctx.putImageData(imageData, 0, 0);

    return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Container # follows ISO 6346: 4 uppercase letters + 7 digits.
// Booking # on these tickets is typically labeled BOOKING / EDO/BOOKING.
function parseExtractedText(rawText) {
  const text = (rawText || "").toUpperCase();

  // Container number (ISO 6346)
  const containerMatch = text.match(/\b[A-Z]{4}\d{7}\b/);

  // Booking number — try to find a token on the same line as a BOOKING label.
  let bookingNo = null;
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (/BOOKING|EDO/.test(line)) {
      const tokens = line.match(/[A-Z0-9]{6,}/g) || [];
      const candidate = tokens.find(
        (t) => !/^BOOKING$/.test(t) && !/^EDO$/.test(t)
      );
      if (candidate) {
        bookingNo = candidate;
        break;
      }
    }
  }
  // Fallback: explicit EBKG-prefixed token anywhere
  if (!bookingNo) {
    const m = text.match(/\bEBKG[A-Z0-9]+\b/);
    if (m) bookingNo = m[0];
  }

  return {
    containerNo: containerMatch ? containerMatch[0] : null,
    bookingNo,
  };
}

export default function ContainerForm() {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [containers, setContainers] = useState([]);
  const [companies, setCompanies] = useState([]); // ✅ Companies for driver dropdown
  const [bookingPOs, setBookingPOs] = useState([]); // POs on the currently selected booking

  const [containerForm, setContainerForm] = useState({
    ContainerNo: "",
    SealNo: "",
    DriverID: null,
    PO: "",
  });

  // ---------- SCAN-PICKUP-TICKET STATE (additive — does not affect manual flow) ----------
  const fileInputRef = useRef(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  const [scanResult, setScanResult] = useState(null); // { containerNo, bookingNo, bookingMatched, previewUrl }

  // -------------------------
  // LOAD BOOKINGS (FOR DROPDOWN)
  // -------------------------
  const loadBookings = async () => {
    try {
      const res = await api.get("/container/list");
      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load bookings:", err);
      setBookings([]);
    }
  };

  // -------------------------
  // LOAD CONTAINERS BY BOOKING
  // -------------------------
  const loadContainers = async (bookingId) => {
    try {
      const res = await api.get(`/container/booking/${bookingId}`);
      setContainers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load containers:", err);
      setContainers([]);
    }
  };

  // -------------------------
  // LOAD COMPANIES (FOR DRIVER)
  // -------------------------
  const loadCompanies = async () => {
    try {
      const res = await api.get("/freight"); // Companies API
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load companies:", err);
      setCompanies([]);
    }
  };

  useEffect(() => {
    loadBookings();
    loadCompanies();
  }, []);

  // Load the booking's PO list so the intake form can tag each container
  // with the PO it carries. Single-PO bookings auto-select; multi-PO bookings
  // force the user to pick.
  const loadBookingPOs = async (bookingId) => {
    try {
      const res = await api.get(`/containerimages/bookinglines/${bookingId}`);
      const lines = Array.isArray(res.data) ? res.data : [];
      const distinct = [...new Set(lines.map((l) => (l.PO || "").trim()).filter(Boolean))];
      setBookingPOs(distinct);
      setContainerForm((prev) => ({
        ...prev,
        PO: distinct.length === 1 ? distinct[0] : "",
      }));
    } catch (err) {
      console.error("Failed to load booking POs:", err);
      setBookingPOs([]);
    }
  };

  useEffect(() => {
    if (selectedBooking?.BookingID) {
      loadContainers(selectedBooking.BookingID);
      loadBookingPOs(selectedBooking.BookingID);
    } else {
      setContainers([]);
      setBookingPOs([]);
    }
  }, [selectedBooking]);

  // -------------------------
  // ADD CONTAINER
  // -------------------------
 const addContainer = async () => {
  if (!selectedBooking) return alert("Select a Booking first");
  if (!containerForm.ContainerNo) return alert("Enter Container Number");
  if (!containerForm.DriverID) return alert("Select a Driver");
  if (bookingPOs.length > 1 && !containerForm.PO) {
    return alert("This booking has multiple POs — pick the PO this container is for");
  }

  try {
    await api.post("/container", {
      BookingID: selectedBooking.BookingID,
      ContainerNo: containerForm.ContainerNo,
      SealNo: containerForm.SealNo,
      DriverID: containerForm.DriverID,
      PO: containerForm.PO,
    });

    // ✅ RELOAD FROM DB so BookingNo + CompanyName show
    await loadContainers(selectedBooking.BookingID);

    setContainerForm({
      ContainerNo: "",
      SealNo: "",
      DriverID: null,
      PO: bookingPOs.length === 1 ? bookingPOs[0] : "",
    });
  } catch (err) {
    console.error("Add container error:", err);
    alert("Failed to add container");
  }
};


  // -------------------------
  // SCAN PICKUP TICKET (OCR — additive, does not modify manual flow)
  // -------------------------
  const handleScanFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    setScanLoading(true);
    setScanError("");
    setScanResult(null);

    const previewUrl = URL.createObjectURL(file);

    try {
      const processed = await preprocessImage(file);
      const { data } = await Tesseract.recognize(processed, "eng", {
        tessedit_char_whitelist:
          "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:/-. \n",
      });
      const extracted = parseExtractedText(data.text);
      // Tesseract returns confidence 0-100. Below ~70 the read is usually
      // unreliable for ISO 6346 codes; we surface the score so the user
      // knows whether to trust the auto-fill before saving.
      const confidence = Math.round(data.confidence ?? 0);

      // Try to auto-select the matching booking from the existing dropdown list.
      let bookingMatched = false;
      if (extracted.bookingNo) {
        const match = bookings.find(
          (b) => (b.BookingNo || "").toUpperCase() === extracted.bookingNo
        );
        if (match) {
          setSelectedBooking(match);
          bookingMatched = true;
        }
      }
      /* A container number carries its own check digit, so we do not have to
       * take Tesseract's word for it. Checking the number is far more
       * reliable than the confidence score: a confident misread scores high
       * and is still wrong, while a nervous but correct read scores low and
       * is still right.
       *
       * So the check digit decides whether to fill the field, and the score
       * is only shown for information. */
      const checked = extracted.containerNo ? validateContainerNo(extracted.containerNo) : null;
      const didYouMean = checked && !checked.ok ? suggestContainerNo(extracted.containerNo) : null;

      if (checked && checked.ok) {
        // The number checks out - safe to fill in, whatever the score said.
        setContainerForm((prev) => ({ ...prev, ContainerNo: checked.value }));
      }

      setScanResult({
        containerNo: extracted.containerNo,
        readAs: checked ? checked.value : null,
        valid: checked ? checked.ok : null,
        reason: checked ? checked.reason : null,
        fixes: checked ? checked.fixes : [],
        didYouMean,
        prettyNumber: checked && checked.ok ? prettyContainerNo(checked.value) : null,
        bookingNo: extracted.bookingNo,
        bookingMatched,
        confidence,
        previewUrl,
      });

      // If container # could not be extracted, alert the user to type manually.
      if (!extracted.containerNo) {
        setScanError(
          "Couldn't read the Container Number from the picture (often the case for handwritten ones). Please type it manually below."
        );
      } else if (confidence < 70) {
        setScanError(
          `Low OCR confidence (${confidence}%). Double-check the Container Number before saving — the scan may be wrong.`
        );
      }
    } catch (err) {
      console.error("OCR error:", err);
      setScanError("Failed to read the picture. Please try again or use manual entry.");
    } finally {
      setScanLoading(false);
    }
  };

  const clearScan = () => {
    if (scanResult?.previewUrl) URL.revokeObjectURL(scanResult.previewUrl);
    setScanResult(null);
    setScanError("");
  };

  // -------------------------
  // DELETE CONTAINER
  // -------------------------
  const deleteContainer = async (containerId) => {
    if (!window.confirm("Delete this container?")) return;
    try {
      await api.delete(`/container/${containerId}`);
      // Re-sync from DB so counts/remaining stay accurate (not just a local filter)
      if (selectedBooking) await loadContainers(selectedBooking.BookingID);
      else setContainers(containers.filter(c => c.ContainerID !== containerId));
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete container");
    }
  };

  return (
    <Box p={2}>
      {/* SELECT BOOKING */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Select Booking</Typography>
        <Autocomplete
          options={bookings}
          getOptionLabel={(o) => o?.BookingNo || ""}
          value={selectedBooking}
          onChange={(e, v) => setSelectedBooking(v)}
          renderInput={(params) => <TextField {...params} label="Booking No" fullWidth />}
          fullWidth
          sx={{ width: "100%", maxWidth: { xs: "100%", md: 400 }, mt: 2 }}
        />
      </Paper>

      {/* OPTIONAL: SCAN PICKUP TICKET (OCR) — additive, does not affect manual entry below */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: "#fafafa" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
          <Box>
            <Typography variant="h6">📷 Scan Pickup Ticket (Optional)</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a photo of the pickup ticket to auto-fill the Booking and Container number.
              You can still type them manually below.
            </Typography>
          </Box>
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleScanFile}
            />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanLoading}
              startIcon={scanLoading ? <CircularProgress size={18} /> : null}
            >
              {scanLoading ? "Reading..." : "Upload from Gallery / Take Photo"}
            </Button>
          </Box>
        </Stack>

        {scanError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {scanError}
          </Alert>
        )}

        {scanResult && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                {scanResult.previewUrl && (
                  <Box
                    component="img"
                    src={scanResult.previewUrl}
                    alt="Scanned ticket"
                    sx={{
                      width: "100%",
                      maxHeight: 220,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                    }}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Extracted values
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`Booking: ${scanResult.bookingNo || "not found"}`}
                    color={scanResult.bookingNo ? (scanResult.bookingMatched ? "success" : "warning") : "default"}
                    variant={scanResult.bookingNo ? "filled" : "outlined"}
                  />
                  <Chip
                    label={
                      scanResult.valid
                        ? `Container: ${scanResult.prettyNumber} ✓ checked`
                        : `Container: ${scanResult.containerNo || "not found"}`
                    }
                    color={scanResult.valid ? "success" : scanResult.containerNo ? "error" : "default"}
                    variant={scanResult.containerNo ? "filled" : "outlined"}
                  />
                  {typeof scanResult.confidence === "number" && (
                    <Chip
                      size="small"
                      label={`OCR confidence: ${scanResult.confidence}%`}
                      color={
                        scanResult.confidence >= 85
                          ? "success"
                          : scanResult.confidence >= 70
                          ? "warning"
                          : "error"
                      }
                      variant="outlined"
                    />
                  )}
                </Stack>
                {/* The number failed its own check digit, so it is wrong -
                  * regardless of how confident Tesseract was. Say so, and if
                  * a single-character correction is the only thing that would
                  * check out, offer it as a question for the user to confirm
                  * against the photo. Never fill it in silently. */}
                {scanResult.containerNo && scanResult.valid === false && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <b>That container number cannot be right.</b> {scanResult.reason}
                    {scanResult.didYouMean && (
                      <Box sx={{ mt: 1 }}>
                        Did you mean <b>{prettyContainerNo(scanResult.didYouMean)}</b>?
                        {" "}Check it against the photo before using it.
                        <Button
                          size="small" variant="outlined" sx={{ ml: 1 }}
                          onClick={() => {
                            setContainerForm((prev) => ({ ...prev, ContainerNo: scanResult.didYouMean }));
                            setScanResult((r) => ({ ...r, valid: true,
                              prettyNumber: prettyContainerNo(scanResult.didYouMean), reason: null }));
                          }}
                        >
                          Yes, use it
                        </Button>
                      </Box>
                    )}
                    {!scanResult.didYouMean && " Please type it in below."}
                  </Alert>
                )}
                {scanResult.valid && scanResult.fixes && scanResult.fixes.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Corrected {scanResult.fixes.join(", ")} — the number checks out.
                  </Alert>
                )}
                {scanResult.bookingNo && !scanResult.bookingMatched && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Booking <b>{scanResult.bookingNo}</b> wasn't found in the dropdown.
                    Please pick it manually from "Select Booking" above.
                  </Alert>
                )}
                <Button size="small" sx={{ mt: 1 }} onClick={clearScan}>
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* ADD CONTAINER */}
      {selectedBooking && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">
            Add Container for Booking: {selectedBooking.BookingNo}
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Container Number"
                fullWidth
                value={containerForm.ContainerNo}
                onChange={(e) =>
                  setContainerForm({ ...containerForm, ContainerNo: e.target.value })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Seal Number"
                fullWidth
                value={containerForm.SealNo}
                onChange={(e) =>
                  setContainerForm({ ...containerForm, SealNo: e.target.value })
                }
              />
            </Grid>

            {bookingPOs.length > 0 && (
              <Grid size={{ xs: 12, md: 2 }}>
                <Autocomplete
                  options={bookingPOs}
                  value={containerForm.PO || null}
                  onChange={(e, v) => setContainerForm({ ...containerForm, PO: v || "" })}
                  disableClearable={bookingPOs.length === 1}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={bookingPOs.length > 1 ? "PO (required)" : "PO"}
                      fullWidth
                      required={bookingPOs.length > 1}
                    />
                  )}
                  fullWidth
                  sx={{ width: "100%" }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: bookingPOs.length > 0 ? 3 : 4 }}>
          <Autocomplete
              options={companies}
              getOptionLabel={(c) => c.CompanyName || ""}
              value={companies.find(c => c.CompanyID === containerForm.DriverID) || null}
              onChange={(e, v) =>
                setContainerForm({
                  ...containerForm,
                  DriverID: v?.CompanyID ? Number(v.CompanyID) : null
                })
              }
              renderInput={(params) => <TextField {...params} label="Driver Company" fullWidth />}
              fullWidth
              sx={{ width: "100%" }}
            />

            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Button fullWidth variant="contained" onClick={addContainer}>
                Add
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* CONTAINER LIST */}
      {selectedBooking && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Containers</Typography>
          <TableContainer sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                  <TableCell>Booking No</TableCell>
                <TableCell>Container No</TableCell>
                <TableCell>Seal No</TableCell>
                <TableCell>PO</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {containers.map((c) => (
                <TableRow key={c.ContainerID}>
                  <TableCell>{c.BookingNo}</TableCell>
                  <TableCell>{c.ContainerNo}</TableCell>
                  <TableCell>{c.SealNo}</TableCell>
                  <TableCell>{c.PO || "-"}</TableCell>
                  <TableCell>{c.CompanyName || "-"}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => deleteContainer(c.ContainerID)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {containers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No containers for this booking
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
